<template>
  <div>
    <div class="pagePopMask pFull" v-if="visible" @click="handleCancel"></div>
    <div class="pagePop w1280 pCenter" v-if="visible">
      <div class="w">
        <div class="tit fnt_32">{{ globalDataArray.siteTemplateLanguage.improve_information2 || '多语言信息' }}</div>
        <div class="form-container">
          <div class="stform" v-if="!loading">
            <a-form-model ref="multiLanguageForm" :model="formData" :rules="formRules">
              <!-- 动态渲染字段 -->
              <template v-if="fieldConfigs.length > 0">
                <div v-for="(field, index) in fieldConfigs" :key="field.id" class="field-group">
                  <div class="field-title">{{ getFieldNameByLanguage(field, currentSystemLanguage) }}</div>
                  <a-row :gutter="16">
                    <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12" v-for="language in supportedLanguages" :key="language.code">
                      <a-form-model-item
                        :label="language.name"
                        :prop="`fieldValues.${field.id}.${language.code}`"
                      >
                        <!-- 根据字段类型渲染不同的输入组件 -->
                        <a-textarea
                          v-if="field.fieldType === 'textarea'"
                          v-model="formData.fieldValues[field.id][language.code]"
                          :placeholder="`${globalDataArray.siteTemplateLanguage.please_enter || '请输入'}${getFieldNameByLanguage(field, currentSystemLanguage)}(${language.name})`"
                          :rows="3"
                          :disabled="hasOriginalValue(field, language.code)"
                        />
                        <a-input
                          v-else
                          v-model="formData.fieldValues[field.id][language.code]"
                          :placeholder="`${globalDataArray.siteTemplateLanguage.please_enter || '请输入'}${getFieldNameByLanguage(field, currentSystemLanguage)}(${language.name})`"
                          :disabled="hasOriginalValue(field, language.code)"
                        />
                      </a-form-model-item>
                    </a-col>
                  </a-row>
                  <!-- 分割线（最后一个字段不显示） -->
                  <div v-if="index < fieldConfigs.length - 1" class="line"></div>
                </div>
              </template>

              <!-- 如果没有字段配置 -->
              <a-row v-else>
                <a-col :xs="24" :sm="24" :md="24" :lg="24" :xl="24">
                  <div style="text-align: center; padding: 40px; color: #999;">
                    {{ globalDataArray.siteTemplateLanguage.system_data_empty_tips || '暂无数据' }}
                  </div>
                </a-col>
              </a-row>
            </a-form-model>
          </div>

          <!-- 加载状态 -->
          <div v-else style="text-align: center; padding: 60px;">
            <a-spin size="large" />
            <div style="margin-top: 16px; color: #999;">{{ globalDataArray.siteTemplateLanguage.loading || 'loading...' }}</div>
          </div>
        </div>
        <div class="btnBox fnt_18">
          <div class="btn btn1" :class="{ 'btn-disabled': loading }" @click="handleSubmitClick">{{ globalDataArray.siteTemplateLanguage.save || '保存' }}</div>
          <div class="btn btn2" @click="handleCancel">{{ globalDataArray.siteTemplateLanguage.cancel || '取消' }}</div>
        </div>
        <div class="close" @click="handleCancel"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserFieldConfigs, saveUserFieldValues } from '@/api/person/certificate'
import Cookies from 'js-cookie'

export default {
  name: 'MultiLanguageInfo',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recordData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      fieldConfigs: [],
      formData: {
        fieldValues: {}
      },
      formRules: {}, // 动态生成的验证规则
      loading: false
    }
  },
  computed: {
    // 支持的语言列表，从全局配置动态获取
    supportedLanguages() {
      let languages = []

      // 从全局配置获取支持的语言列表
      if (this.globalDataArray && this.globalDataArray.siteLanguage && this.globalDataArray.siteLanguage.length > 0) {
        languages = this.globalDataArray.siteLanguage.map(lang => ({
          code: lang.value,
          name: lang.title
        }))
      } else {
        // 如果全局配置不可用，使用默认配置
        languages = [
          { code: 'zh_CN', name: '中文' },
          { code: 'en_US', name: 'English' }
        ]
      }

      // 将当前系统语言排在第一位
      const currentLang = this.currentSystemLanguage
      const currentIndex = languages.findIndex(lang => lang.code === currentLang)
      if (currentIndex > 0) {
        const currentLangObj = languages.splice(currentIndex, 1)[0]
        languages.unshift(currentLangObj)
      }

      return languages
    },

    // 当前系统语言
    currentSystemLanguage() {
      // 优先从 Cookie 获取
      const cookieLang = Cookies.get('DEFAULT_LANG')
      if (cookieLang) {
        return cookieLang
      }

      // 然后从全局配置获取
      if (this.globalDataArray && this.globalDataArray.siteInfo && this.globalDataArray.siteInfo.language) {
        return this.globalDataArray.siteInfo.language
      }

      // 最后使用默认值
      return 'zh_CN'
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadFieldConfigs()
      } else {
        this.resetForm()
      }
    }
  },
  methods: {
    async loadFieldConfigs() {
      if (!this.recordData.id) {
        this.$message.error(this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试')
        return
      }

      // ✅ 修复：从报名记录中获取正确的比赛ID
      // 优先级：competitionId > competition_id > parent_competition_id
      const competitionId = this.recordData.competitionId ||
                         this.recordData.competition_id ||
                         this.recordData.parent_competition_id

      if (!competitionId) {
        this.$message.error(this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试')
        console.error('报名记录数据:', this.recordData)
        return
      }

      try {
        this.loading = true
        const params = {
          signupId: this.recordData.id,
          competitionId: competitionId
        }

        const res = await getUserFieldConfigs(params)
        if (res.success) {
          this.fieldConfigs = res.result.fieldInfos || []
          this.initFormData()
        } else {
          this.$message.error(res.message || (this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试'))
        }
      } catch (error) {
        console.error('加载字段配置失败', error)
        this.$message.error(this.globalDataArray.siteTemplateLanguage.system_parameter_error_tips || '参数有误，请刷新重试')
      } finally {
        this.loading = false
      }
    },

    initFormData() {
      // 初始化表单数据结构
      const fieldValues = {}

      this.fieldConfigs.forEach(field => {
        fieldValues[field.id] = {}

        // 为每个支持的语言初始化值
        this.supportedLanguages.forEach(language => {
          let value = ''

          // 优先级：已保存的多语言值 > 原始值 > 空值
          if (field.languageValues && field.languageValues[language.code]) {
            // 已保存的多语言值
            value = field.languageValues[language.code]
          } else if (field.originalValue && field.originalLanguage === language.code) {
            // 如果有原始值且语言匹配，使用原始值
            value = field.originalValue
          } else if (field.originalValue && language.code === this.currentSystemLanguage && !field.originalLanguage) {
            // 如果有原始值但没有指定语言，默认作为当前系统语言
            value = field.originalValue
          } else if (field.originalValue && language.code === 'zh_CN' && !field.originalLanguage && !this.currentSystemLanguage) {
            // 如果没有系统语言配置，默认作为中文
            value = field.originalValue
          }

          fieldValues[field.id][language.code] = value
        })
      })

      this.formData.fieldValues = fieldValues

      // 初始化验证规则
      this.initFormRules()
    },

    handleSubmitClick() {
      if (this.loading) {
        return
      }
      this.handleSubmit()
    },

    async handleSubmit() {
      if (this.loading) return

      try {
        // 使用 Ant Design Vue 的表单验证，不显示错误消息，只触发字段验证
        await this.$refs.multiLanguageForm.validate()

        this.loading = true

        // 构造保存数据
        const fieldValues = []
        Object.keys(this.formData.fieldValues).forEach(fieldId => {
          const languageValues = {}
          this.supportedLanguages.forEach(language => {
            const value = this.formData.fieldValues[fieldId][language.code]
            if (value && value.trim()) {
              languageValues[language.code] = value.trim()
            }
          })
          if (Object.keys(languageValues).length > 0) {
            fieldValues.push({
              fieldConfigId: fieldId,
              languageValues: languageValues
            })
          }
        })
        const competitionId = this.recordData.competitionId ||
                           this.recordData.competition_id ||
                           this.recordData.parent_competition_id
        const saveData = {
          signupId: this.recordData.id,
          competitionId: competitionId,
          fieldValues: fieldValues
        }
        const res = await saveUserFieldValues(saveData)
        if (res.success) {
          this.$message.success(this.globalDataArray.siteTemplateLanguage.system_save_success_tips || '保存成功')
          this.handleCancel()
        } else {
          this.$message.error(res.message || (this.globalDataArray.siteTemplateLanguage.save_failed || '保存失败'))
        }
      } catch (error) {
        // 验证失败时，不显示错误消息，让 Ant Design Vue 显示字段级别的验证提示
        return
      } finally {
        this.loading = false
      }
    },

    handleCancel() {
      this.$emit('update:visible', false)
      this.resetForm()
    },

    resetForm() {
      this.fieldConfigs = []
      this.formData = {
        fieldValues: {}
      }
      this.formRules = {}
    },

    // 检查字段是否有原始值
    hasOriginalValue(field, languageCode) {
      return field.originalValue && field.originalLanguage === languageCode
    },

    // 根据语言获取字段名称
    getFieldNameByLanguage(field, language) {
      if (field.fieldNameMulti && field.fieldNameMulti[language]) {
        return field.fieldNameMulti[language]
      }
      return field.fieldName // 默认字段名称
    },

    // 初始化表单验证规则
    initFormRules() {
      const formRules = {}

      this.fieldConfigs.forEach(field => {
        if (field.isRequired) {
          this.supportedLanguages.forEach(language => {
            formRules[`fieldValues.${field.id}.${language.code}`] = [{
              required: true,
              message: `${this.globalDataArray.siteTemplateLanguage.please_enter || '请输入'}${this.getFieldNameByLanguage(field, this.currentSystemLanguage)}(${language.name})`,
              trigger: ['change', 'blur']
            }]
          })
        }
      })

      this.formRules = formRules
    }
  }
}
</script>

<style scoped>
/* 表单容器支持滚动 */
.form-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
}

/* 自定义滚动条样式 */
.form-container::-webkit-scrollbar {
  width: 6px;
}

.form-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.form-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.form-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.field-group {
  margin-bottom: 32px;
  padding: 0;
}

.field-title {
  font-size: 18px;
  font-weight: bold;
  color: #1890ff;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #1890ff;
  position: relative;
}

.field-title::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 20px;
  height: 2px;
  background: #52c41a;
}

.line {
  height: 1px;
  background: #e8e8e8;
  margin: 24px auto;
  width: 80%;
}

/* 表单项样式优化 */
::v-deep .ant-form-item-label {
  font-weight: 600;
  color: #333;
}

::v-deep .ant-input,
::v-deep .ant-input:focus {
  border-radius: 6px;
  transition: all 0.3s ease;
}

::v-deep .ant-input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 禁用状态的样式 */
::v-deep .ant-input[disabled],
::v-deep .ant-input-disabled {
  background-color: #f5f5f5;
  border-color: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}

/* 复用已有的样式 */
.btn-disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}
</style>
